import { Component, Input, Output, EventEmitter, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { Carriers } from '../../../interfaces/Carriers.interfaces';
import { ShipmentManagementService } from '../../../services/shipment-management.service';

@Component({
  selector: 'app-carrier-details-popup',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './carrier-details-popup.component.html',
  styleUrl: './carrier-details-popup.component.css'
})
export class CarrierDetailsPopupComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible: boolean = false;
  @Input() carrierCode: string | null = null;
  @Output() closed = new EventEmitter<void>();

  carrier: Carriers | null = null;
  isLoading = false;
  hasError = false;

  private subscription: Subscription | null = null;
  private readonly keyHandler = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.visible) {
      this.close();
    }
  };

  constructor(private shipmentManagementService: ShipmentManagementService) {}

  ngOnInit(): void {
    document.addEventListener('keydown', this.keyHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] || changes['carrierCode']) {
      if (this.visible && this.carrierCode) {
        this.loadCarrier(this.carrierCode);
      } else if (!this.visible) {
        this.subscription?.unsubscribe();
        this.carrier = null;
        this.isLoading = false;
        this.hasError = false;
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.keyHandler);
    this.subscription?.unsubscribe();
  }

  private loadCarrier(carrierCode: string): void {
    this.subscription?.unsubscribe();
    this.carrier = null;
    this.hasError = false;
    this.isLoading = true;

    this.subscription = this.shipmentManagementService.getCarrierDetails(carrierCode).subscribe({
      next: carrier => {
        this.carrier = carrier;
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      }
    });
  }

  close(): void {
    this.closed.emit();
  }

  getStatusBadgeClass(): string {
    switch (this.carrier?.carrierStatus) {
      case 'ACTIVE':
        return 'status-executed';
      case 'PENDING':
        return 'status-pending';
      case 'SUSPENDED':
      case 'INACTIVE':
        return 'status-failed';
      default:
        return 'status-unknown';
    }
  }

  copyToClipboard(text: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).catch(err => console.error('Failed to copy text: ', err));
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    }
  }

  hasLegalAddress(): boolean {
    return !!(this.carrier?.carrierLegalAddressStreet || this.carrier?.carrierLegalAddressCity);
  }

  hasOperatingAddress(): boolean {
    return !!(this.carrier?.carrierOperatingAddressStreet || this.carrier?.carrierOperatingAddressCity);
  }

  hasCoordinates(): boolean {
    return this.carrier?.carrierLatitude != null && this.carrier?.carrierLongitude != null;
  }

  getCoordinatesMapUrl(): string {
    return this.hasCoordinates()
      ? `https://www.google.com/maps?q=${this.carrier!.carrierLatitude},${this.carrier!.carrierLongitude}&z=15`
      : '';
  }
}
